<template>
  <div>
    <!-- STEP 1: choose activities (same type only) + students -->
    <a-form v-show="step === 1" :form="form" class="card-header p-5">
      <p class="text-muted mb-3">{{ $t("inscriptionActivites.multipleRegistrationStep1Subtitle") }}</p>
      <div class="row">
        <div class="col-md-12">
          <a-form-item
            :validate-status="
              selectedActivityIds.length === 0 && activitySelectTouched
                ? 'error'
                : ''
            "
            :help="
              selectedActivityIds.length === 0 && activitySelectTouched
                ? $t('paiement.activiteRequis')
                : ''
            "
          >
            <a-select
              :value="selectedActivityIds"
              show-search
              mode="multiple"
              :allowClear="true"
              :placeholder="
                loadingActivities
                  ? $t('all.loadingText')
                  : $t('paiement.listeActivites')
              "
              :disabled="loadingActivities"
              style="width: 100%"
              option-filter-prop="title"
              :default-active-first-option="false"
              :maxTagCount="5"
              @change="selectActivity"
              @blur="activitySelectTouched = true"
            >
              <a-select-option
                v-for="activtiy in activities"
                :key="activtiy._id"
                :value="activtiy._id"
                :title="activtiy.designation"
                :disabled="!!incompatibleActivityIds[activtiy._id]"
              >
                <span>{{ activtiy.designation }}</span>
                <a-tag
                  class="activity-type-tag"
                  :color="activtiy.daily ? 'blue' : 'green'"
                >
                  {{
                    activtiy.daily
                      ? $t("paiement.tagActiviteJournaliere")
                      : $t("paiement.tagActiviteMensuelleTranche")
                  }}
                </a-tag>
              </a-select-option>
            </a-select>
          </a-form-item>
        </div>
        <div class="col-md-12">
          <StudentSelectorFields
            v-model="selectionMode"
            :student-list="studentList"
            :classes="classes"
            :students-data="studentsData"
            :level-select-data="levelSelectData"
            :loading-students="loadingStudents"
            :loading-classes="loadingClasses"
            @level-students-change="selectedLevelStudents = $event"
          />
        </div>
      </div>
      <div class="row">
        <div class="col-md-12 mt-3">
          <a-button
            type="primary"
            style="width: 100%"
            icon="arrow-right"
            @click="proceedToConfig"
          >
            {{ $t("action.suivant") }}
          </a-button>
        </div>
      </div>
    </a-form>

    <!-- STEP 2: configure each activity separately -->
    <div v-if="step === 2" class="p-5">
      <p class="text-muted mb-3">{{ $t("inscriptionActivites.multipleRegistrationStep2Subtitle") }}</p>

      <a-empty
        v-if="configuredActivities.length === 0"
        :description="$t('inscriptionActivites.multipleRegistrationNoActivitiesSelected')"
      />

      <a-collapse
        v-else
        :default-active-key="selectedActivityIds"
        expand-icon-position="right"
      >
        <a-collapse-panel
          v-for="activity in configuredActivities"
          :key="activity._id"
        >
          <template slot="header">
            <strong>{{ activity.designation }}</strong>
            <a-tag
              class="activity-type-tag"
              :color="activity.daily ? 'blue' : 'green'"
            >
              {{
                activity.daily
                  ? $t("paiement.tagActiviteJournaliere")
                  : $t("paiement.tagActiviteMensuelleTranche")
              }}
            </a-tag>
          </template>

          <div class="row">
            <div class="col-md-6">
              <label>{{ $t("paiement.dateDebutRequis") }}</label>
              <a-date-picker
                :allowClear="false"
                :value="activityConfigs[activity._id].start"
                :disabled-date="(current) => disabledActivityStartDate(current, activity)"
                :format="dateFormat"
                style="width: 100%"
                @change="(v) => onActivityStartChange(activity, v)"
              />
            </div>
            <div class="col-md-6">
              <label>{{ $t("paiement.dateFinRequis") }}</label>
              <a-date-picker
                :value="activityConfigs[activity._id].end"
                :disabled-date="(current) => disabledActivityEndDate(current, activity)"
                :format="dateFormat"
                style="width: 100%"
                @change="(v) => onActivityEndChange(activity, v)"
              />
            </div>
          </div>

          <!-- Monthly / Tranche mode selection -->
          <MonthlyFeesSection
            v-if="!activity.daily"
            :activity="activity"
            :mode="activityConfigs[activity._id].mode"
            :included-fees="activityConfigs[activity._id].includedFees"
            @change-mode="(mode) => changeActivityMode(activity, mode)"
            @toggle-fee="(key, e) => toggleIncludedFee(activity._id, key, e)"
          />

          <!-- Daily activity: fees + calendar -->
          <DailyFeesSection
            v-else
            :activity="activity"
            :selected-dates="activityConfigs[activity._id].selectedDailyDates"
            :activity-start="getActivityBounds(activity).start"
            :activity-end="getActivityBounds(activity).end"
            @add-date="(date) => addSelectedDailyDate(activity._id, date)"
            @remove-date="(date) => removeSelectedDailyDate(activity._id, date)"
            @clear-all-dates="clearAllDailyDates(activity._id)"
          />
        </a-collapse-panel>
      </a-collapse>

      <div class="row">
        <div class="col-md-6 mt-4">
          <a-button style="width: 100%" icon="arrow-left" @click="step = 1">
            {{ $t("action.retour") }}
          </a-button>
        </div>
        <div class="col-md-6 mt-4">
          <a-button
            type="primary"
            style="width: 100%"
            icon="tags"
            :loading="loadingClasse"
            :disabled="loadingClasse"
            @click="submitAll"
          >
            {{ $t("action.enregistrer") }}
          </a-button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
/* eslint-disable */
import apiClient from "@/services/axios";
import moment from "moment";
import { mapState } from "vuex";
import { getLevels } from "@/config/LevelConstants";
import store from "store";
import { dateRange, dailyDateRange } from "@/utilities/settings";
import StudentSelectorFields from "./components/StudentSelectorFields.vue";
import MonthlyFeesSection from "./components/MonthlyFeesSection.vue";
import DailyFeesSection from "./components/DailyFeesSection.vue";

export default {
  components: {
    StudentSelectorFields,
    MonthlyFeesSection,
    DailyFeesSection,
  },
  computed: {
    ...mapState(["settings", "user"]),
    activeBuilding() {
      return this.user.building.find(
        (el) => el.dbName == this.settings.activeBuilding
      );
    },
    countryCode() {
      return this.activeBuilding?.countryCode;
    },
    levelSelectData() {
      return getLevels(this.$t.bind(this), this.schoolType, this.countryCode);
    },
    selectedActivities() {
      return this.activities.filter((ac) =>
        this.selectedActivityIds.includes(ac._id)
      );
    },
    incompatibleActivityIds() {
      if (!this.selectedActivityIds.length) return {};
      const reference = this.activities.find(
        (ac) => ac._id === this.selectedActivityIds[0]
      );
      if (!reference) return {};
      const refDaily = !!reference.daily;
      const map = {};
      this.activities.forEach((a) => {
        if (!!a.daily !== refDaily) map[a._id] = true;
      });
      return map;
    },
    configuredActivities() {
      return this.selectedActivities.filter(
        (ac) => !!this.activityConfigs[ac._id]
      );
    },
  },

  async created() {
    this.schoolType = this.activeBuilding.type;

    this.form = this.$form.createForm(this, { name: "addMultipleRegistrationForm" });
    apiClient
      .post("/activities/filter", {
        query: {
          status: "active",
        },
        aggregation: {
          $project: {
            _id: 1,
            designation: 1,
            start: 1,
            end: 1,
            inscription: 1,
            monthly: 1,
            once: 1,
            tranches: 1,
            daily: 1,
          },
        },
      })
      .then((res) => (this.activities = res.data))
      .catch((e) => this.$message.error(this.$t("error.aucActivite")))
      .finally(() => {
        this.loadingActivities = false;
      });
    apiClient
      .post("/students/filter", {
        query: {
          status: "active",
        },
        aggregation: [
          {
            $set: {
              classRoom: {
                $convert: {
                  input: `$schoolarYearsHistory.${this.settings.activeSchoolarYear}`,
                  to: "objectId",
                  onError: null,
                  onNull: null,
                },
              },
            },
          },
          {
            $set: {
              classRoomName: "$classRoom",
            },
          },
          {
            $lookup: {
              from: "classrooms",
              localField: "classRoom",
              foreignField: "_id",
              as: "classRoomName",
            },
          },
          {
            $project: {
              _id: 1,
              classRoom: 1,
              firstName: 1,
              middleName: 1,
              lastName: 1,
              classRoomName: { $arrayElemAt: ["$classRoomName.name", 0] },
            },
          },
        ],
      })
      .then((res) => {
        const { data } = res;
        this.studentList = data;
        this.studentsData = _.mapValues(_.groupBy(data, "classRoom"), (clist) =>
          clist.map((elem) => _.omit(elem, "classRoom"))
        );
      })
      .catch((e) => this.$message.error(this.$t("error.aucEleve")))
      .finally(() => {
        this.loadingStudents = false;
      });
    await apiClient
      .post("/classrooms/filter", {
        query: {
          status: "active",
        },
      })
      .then((res) => {
        this.classes = res.data;
      })
      .catch((e) => this.$message.error(this.$t("error.aucClasse")))
      .finally(() => {
        this.loadingClasses = false;
      });
    await this.fetchActivityPayments();
  },

  data() {
    return {
      step: 1,
      selectionMode: "students",
      loadingClasse: false,
      dateFormat: "DD/MM/YYYY",
      studentsData: {},
      studentList: [],
      classes: [],
      activities: [],
      selectedActivityIds: [],
      activitySelectTouched: false,
      selectedLevelStudents: [],
      studentsToProcess: [],
      activityConfigs: {},
      actvitiyPayments: [],
      schoolType: "",
      loadingStudents: true,
      loadingActivities: true,
      loadingClasses: true,
    };
  },

  watch: {
    selectionMode() {
      this.form.resetFields(["students", "classes", "levels"]);
    },
  },

  methods: {
    dateRange,
    dailyDateRange,
    moment,

    getActivityBounds(activity) {
      const schoolarYears = this.settings.schoolarYears || [];
      const activeSchoolarYear = store.get("app.settings.activeSchoolarYear");
      const schoolarYear = schoolarYears.find(
        (sc) => sc._id == activeSchoolarYear
      );
      const start = activity.start
        ? moment(activity.start)
        : moment(schoolarYear && schoolarYear.start);
      const end = activity.end
        ? moment(activity.end)
        : moment(schoolarYear && schoolarYear.end);
      return { start, end };
    },

    disabledActivityStartDate(current, activity) {
      const bounds = this.getActivityBounds(activity);
      return (
        (current && current.isAfter(bounds.end, "day")) ||
        current.isBefore(bounds.start, "day")
      );
    },
    disabledActivityEndDate(current, activity) {
      const bounds = this.getActivityBounds(activity);
      const cfg = this.activityConfigs[activity._id];
      const start = cfg && cfg.start ? cfg.start : bounds.start;
      return (
        (current && current.isAfter(bounds.end, "day")) ||
        current.isBefore(start, "day")
      );
    },

    // Controlled select: only update selectedActivityIds when the new
    // selection is valid. If invalid, keep the previous value (so the
    // incompatible activity never appears in the select) and show an error.
    selectActivity(vals) {
      const values = Array.isArray(vals) ? vals : vals ? [vals] : [];
      this.activitySelectTouched = true;

      if (values.length === 0) {
        this.selectedActivityIds = [];
        return;
      }

      // Removals are always allowed
      if (values.length < this.selectedActivityIds.length) {
        this.selectedActivityIds = values;
        return;
      }

      const referenceId = this.selectedActivityIds[0] || values[0];
      const referenceActivity = this.activities.find(
        (ac) => ac._id === referenceId
      );
      const referenceIsDaily = !!(referenceActivity && referenceActivity.daily);

      const hasIncompatible = values.some((id) => {
        const ac = this.activities.find((a) => a._id === id);
        return !ac || !!ac.daily !== referenceIsDaily;
      });

      if (hasIncompatible) {
        this.$message.error(this.$t("paiement.activitesTypeDifferent"));
        // Do not update selectedActivityIds — controlled :value keeps the UI
        // on the previous valid selection only.
        return;
      }

      this.selectedActivityIds = values;
    },

    initActivityConfig(activity) {
      const bounds = this.getActivityBounds(activity);
      this.$set(this.activityConfigs, activity._id, {
        start: bounds.start,
        end: activity.end ? bounds.end : null,
        mode: "M",
        includedFees: {},
        selectedDailyDates: [],
      });
      this.recalcIncludedFees(activity._id);
    },

    proceedToConfig() {
      this.activitySelectTouched = true;
      this.form.validateFieldsAndScroll(async (err, values) => {
        if (this.selectedActivityIds.length === 0) {
          this.$message.warning(this.$t("paiement.activiteRequis"));
          return;
        }
        if (err) return;

        let studentsToProcess = [];
        if (this.selectionMode === "students") {
          studentsToProcess = values.students;
        } else if (this.selectionMode === "classes") {
          values.classes.forEach((classId) => {
            const classStudents = this.studentsData[classId] || [];
            studentsToProcess.push(...classStudents.map((s) => s._id));
          });
        } else if (this.selectionMode === "levels") {
          studentsToProcess = this.selectedLevelStudents || [];
        }

        if (studentsToProcess.length === 0) {
          this.$message.warning(this.$t("error.aucEleve"));
          return;
        }

        this.studentsToProcess = [...new Set(studentsToProcess)];

        this.selectedActivities.forEach((activity) => {
          this.initActivityConfig(activity);
        });

        this.step = 2;
      });
    },

    onActivityStartChange(activity, value) {
      const cfg = this.activityConfigs[activity._id];
      cfg.start = value;
      this.recalcIncludedFees(activity._id);
    },
    onActivityEndChange(activity, value) {
      const cfg = this.activityConfigs[activity._id];
      cfg.end = value;
      this.recalcIncludedFees(activity._id);
    },
    changeActivityMode(activity, mode) {
      const cfg = this.activityConfigs[activity._id];
      cfg.mode = mode;
      this.recalcIncludedFees(activity._id);
    },
    toggleIncludedFee(activityId, key, value) {
      this.$set(this.activityConfigs[activityId].includedFees, key, value);
    },
    recalcIncludedFees(activityId) {
      const activity = this.activities.find((a) => a._id === activityId);
      const cfg = this.activityConfigs[activityId];
      if (!activity || !cfg || activity.daily) return;

      const bounds = this.getActivityBounds(activity);
      const start = cfg.start || bounds.start;
      const end = cfg.end || bounds.end;
      const range = this.dateRange(
        moment(start).format("YYYY-MM-DD"),
        moment(end).format("YYYY-MM-DD")
      );

      const includedFees = {};
      if (activity.monthly && cfg.mode === "M") {
        range.forEach((month) => {
          includedFees[month] = true;
        });
      }
      if (activity.tranches && cfg.mode === "T") {
        activity.tranches.forEach((tranche) => {
          includedFees[tranche.trancheNumber] = true;
        });
      }
      this.$set(cfg, "includedFees", includedFees);
    },

    addSelectedDailyDate(activityId, date) {
      const cfg = this.activityConfigs[activityId];
      if (cfg && !cfg.selectedDailyDates.includes(date)) {
        cfg.selectedDailyDates.push(date);
      }
    },
    removeSelectedDailyDate(activityId, date) {
      const cfg = this.activityConfigs[activityId];
      if (!cfg) return;
      const index = cfg.selectedDailyDates.indexOf(date);
      if (index > -1) cfg.selectedDailyDates.splice(index, 1);
    },
    clearAllDailyDates(activityId) {
      const cfg = this.activityConfigs[activityId];
      if (cfg) cfg.selectedDailyDates = [];
    },

    async fetchActivityPayments() {
      try {
        const response = await apiClient.get(
          "/payment/etatImpaye/activity/all"
        );
        this.actvitiyPayments = response.data
          .filter((p) => p && p.studentId && p._id)
          .map((p) => ({
            id: p._id,
            studentId: p.studentId,
            classroom: p.classroom,
            student: p.student,
            name: p.name,
          }));
      } catch (error) {
        this.$message.error(this.$t("error.fetchingData"));
        this.actvitiyPayments = [];
      }
    },

    validateStudentActivity(studentId, activityId) {
      if (!studentId || !activityId) return false;

      const selectedActivity = this.activities.find(
        (a) => a._id === activityId
      );
      if (!selectedActivity) return true;

      // One-time activities can be registered many times for the same student
      if (selectedActivity.once) return true;

      const existingActivity = this.actvitiyPayments.find(
        (payment) =>
          payment.studentId === studentId &&
          payment.name.toLowerCase() ===
            selectedActivity.designation.toLowerCase()
      );

      return !existingActivity;
    },

    async getStudentsWithoutActivity(studentIds, activityId) {
      const eligibleStudents = [];
      const ineligibleStudents = [];

      if (!Array.isArray(studentIds)) {
        return { eligibleStudents: [], ineligibleStudents: [] };
      }

      for (const studentId of studentIds) {
        if (!studentId) continue;

        const isEligible = this.validateStudentActivity(studentId, activityId);
        const existingPayment = this.actvitiyPayments.find(
          (p) => p.studentId === studentId
        );
        const student = this.studentList.find((s) => s._id === studentId);

        if (isEligible) {
          eligibleStudents.push(studentId);
        } else {
          ineligibleStudents.push({
            id: studentId,
            name: existingPayment
              ? existingPayment.student
              : student
              ? `${student.firstName} ${student.middleName ? student.middleName + " " : ""}${student.lastName}`
              : studentId,
          });
        }
      }

      return { eligibleStudents, ineligibleStudents };
    },

    async submitAll() {
      if (this.selectedActivities.length === 0) {
        this.$message.warning(
          this.$t("inscriptionActivites.multipleRegistrationNoActivitiesSelected")
        );
        return;
      }

      for (const activity of this.selectedActivities) {
        const cfg = this.activityConfigs[activity._id];
        if (!cfg || !cfg.start) {
          this.$message.warning(
            `${activity.designation} : ${this.$t("paiement.dateDebutRequis")}`
          );
          return;
        }
        if (activity.daily && cfg.selectedDailyDates.length === 0) {
          this.$message.warning(
            `${activity.designation} : ${this.$t("etablissement.selectionnerAuMoinsUnJour")}`
          );
          return;
        }
      }

      this.loadingClasse = true;

      try {
        const registrations = [];
        const ineligibleByActivity = [];

        for (const activity of this.selectedActivities) {
          const cfg = this.activityConfigs[activity._id];

          const { eligibleStudents, ineligibleStudents } =
            await this.getStudentsWithoutActivity(
              this.studentsToProcess,
              activity._id
            );

          if (ineligibleStudents.length > 0) {
            ineligibleByActivity.push({ activity, ineligibleStudents });
          }

          if (eligibleStudents.length === 0) {
            continue;
          }

          const mode = activity.daily ? "M" : cfg.mode;

          let feesMap;
          if (activity.daily) {
            feesMap = cfg.selectedDailyDates.reduce((obj, date) => {
              obj[date] = false;
              return obj;
            }, {});
          } else {
            feesMap = Object.keys(cfg.includedFees)
              .filter((key) => cfg.includedFees[key] === true)
              .reduce((obj, key) => {
                obj[key] = false;
                return obj;
              }, {});
          }

          const data = {
            start: cfg.start,
            end: cfg.end || activity.end || undefined,
            students: eligibleStudents,
            activity: activity._id,
            typeAS: "A",
            mode,
            repReduction: Object.keys(feesMap).reduce(
              (obj, key) => {
                obj[key] = 0;
                return obj;
              },
              { inscription: 0 }
            ),
          };

          if (mode === "M") {
            data.months = feesMap;
          } else {
            data.tranches = feesMap;
          }

          registrations.push(data);
        }

        if (registrations.length === 0) {
          ineligibleByActivity.forEach((r) => {
            const ineligibleNames = r.ineligibleStudents
              .map((student) => student.name)
              .join(", ");
            this.$message.warning(
              `${r.activity.designation} - ${this.$t(
                "error.eleveDejaInscritPourMois"
              )} : ${ineligibleNames}`
            );
          });
          this.$message.warning(this.$t("error.aucEleve"));
          return;
        }

        await apiClient.put("/payment/registerStudents/multiple", {
          registrations,
        });

        await this.fetchActivityPayments();

        this.$message.success(
          this.$t("paiement.inscriptionReussitePourActivites", {
            count: registrations.length,
          })
        );

        ineligibleByActivity.forEach((r) => {
          const ineligibleNames = r.ineligibleStudents
            .map((student) => student.name)
            .join(", ");
          this.$message.warning(
            `${r.activity.designation} - ${this.$t(
              "error.eleveDejaInscritPourMois"
            )} : ${ineligibleNames}`
          );
        });

        this.$emit("updateActivityInscription");
        this.resetAll();
      } catch (error) {
        console.error(error);
        this.$message.error(this.$t("error.inscriEchoue"));
      } finally {
        this.loadingClasse = false;
      }
    },

    resetAll() {
      this.step = 1;
      this.selectionMode = "students";
      this.selectedActivityIds = [];
      this.activitySelectTouched = false;
      this.activityConfigs = {};
      this.studentsToProcess = [];
      this.selectedLevelStudents = [];
      if (this.form) {
        this.form.resetFields();
      }
    },
  },
};
</script>
<style scoped>
.activity-type-tag {
  margin-left: 8px;
  font-size: 11px;
  line-height: 18px;
  vertical-align: middle;
}
</style>
